import React, { useState, useRef, useEffect } from 'react';

interface MobileCarouselProps {
  items: React.ReactNode[];
  className?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}

export const MobileCarousel: React.FC<MobileCarouselProps> = ({ 
  items, 
  className = "",
  ariaLabel = "carousel",
  children 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const scrollWidth = scrollRef.current.scrollWidth;
          const itemWidth = scrollWidth / items.length;
          const scrollPosition = index * itemWidth;
          
          scrollRef.current.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      });
    }
    setCurrentIndex(index);
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const scrollLeft = scrollRef.current.scrollLeft;
          const itemWidth = scrollRef.current.scrollWidth / items.length;
          const newIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newIndex);
        }
      });
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className={`w-full ${className}`}>
      {children}
      
      <div 
        ref={scrollRef}
        className="flex overflow-x-auto gap-4 px-4 pb-2 scroll-smooth"
        style={{
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch'
        }}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="flex-none w-[85vw] scroll-snap-center"
            style={{ scrollSnapAlign: 'center' }}
          >
            {item}
          </div>
        ))}
      </div>

      {/* Pagination dots */}
      {items.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-6 h-6 rounded-full transition-colors duration-300 flex items-center justify-center ${
                index === currentIndex ? 'bg-primary' : 'bg-primary/30'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className={`w-2 h-2 rounded-full ${
                index === currentIndex ? 'bg-background' : 'bg-primary'
              }`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};